import {
  TRANSCRIPT_MODE_LOCKER_SCOPED,
  normalizeServerTranscriptMode,
} from './chatTranscriptPolicy';

const DEFAULT_STREAMING_BASE_URL =
  'https://claude-streaming-service-103376361923.us-central1.run.app';
const CHAT_ID_RE = /^[A-Za-z0-9_-]{1,160}$/;
const SEGMENT_ID_RE = /^[A-Za-z0-9._:-]{1,160}$/;
const MESSAGE_ID_RE = /^[^/]{1,512}$/;
const MAX_MESSAGE_BYTES = 128 * 1024;
const MAX_SNAPSHOT_BYTES = 4 * 1024 * 1024;
const MAX_ASSISTANT_SEGMENTS = 1500;
const FATAL_SCOPE_STATUSES = new Set([401, 403, 409]);
const FATAL_SCOPE_CODES = new Set([
  'transcript_scope_unverified',
  'malformed_transcript_snapshot',
  'transcript_snapshot_too_large',
  'transcript_message_too_large',
]);

function utf8ByteLength(value) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value).length;
  }
  if (typeof Blob !== 'undefined') {
    return new Blob([value]).size;
  }
  return value.length * 2;
}

export class ScopedTranscriptError extends Error {
  constructor(code, message, status = null) {
    super(message);
    this.name = 'ScopedTranscriptError';
    this.code = code;
    this.status = status;
  }
}

function requireChatId(chatId) {
  if (!CHAT_ID_RE.test(chatId || '')) {
    throw new ScopedTranscriptError(
      'invalid_chat_id',
      'The secure transcript chat is invalid.'
    );
  }
  return chatId;
}

function requireAuthToken(authToken) {
  if (typeof authToken !== 'string' || authToken.length < 1) {
    throw new ScopedTranscriptError(
      'authentication_required',
      'Authentication is required for the secure transcript.'
    );
  }
  return authToken;
}

function streamingBaseUrl(baseUrl) {
  return (
    baseUrl
    || process.env.REACT_APP_STREAMING_SERVICE_URL
    || DEFAULT_STREAMING_BASE_URL
  ).replace(/\/+$/, '');
}

function parseTimestamp(value) {
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value.toDate === 'function') {
    return value.toDate().toISOString();
  }
  if (value && typeof value.toMillis === 'function') {
    return new Date(value.toMillis()).toISOString();
  }
  if (typeof value === 'number') {
    return new Date(value).toISOString();
  }
  if (typeof value === 'string' && Number.isFinite(Date.parse(value))) {
    return new Date(value).toISOString();
  }
  return null;
}

function deserializeScopedTranscriptMessage(message) {
  if (!message || typeof message !== 'object' || Array.isArray(message)) return null;
  const messageId = typeof message.messageId === 'string'
    ? message.messageId
    : message.id;
  if (typeof messageId !== 'string' || messageId.length < 1) return null;
  const timestamp = parseTimestamp(message.timestamp || message.createdAt);
  if (!timestamp) return null;
  if (
    !['user', 'ai', 'system'].includes(message.sender)
    || typeof message.text !== 'string'
  ) return null;
  let components = message.text
    ? [{ type: 'text', content: message.text, timestamp }]
    : [];
  if (message.segments !== undefined) {
    if (
      message.sender !== 'ai'
      || !Array.isArray(message.segments)
      || message.segments.length > MAX_ASSISTANT_SEGMENTS
    ) return null;
    const segmentIds = new Set();
    const steeredMessageIds = new Set();
    const assistantText = [];
    components = [];
    for (const segment of message.segments) {
      if (
        !segment
        || typeof segment !== 'object'
        || Array.isArray(segment)
        || !['assistant_text', 'steered_user_message'].includes(segment.type)
        || !SEGMENT_ID_RE.test(segment.segmentId || '')
        || segmentIds.has(segment.segmentId)
        || typeof segment.text !== 'string'
      ) return null;
      const allowedKeys = segment.type === 'assistant_text'
        ? new Set(['type', 'segmentId', 'text'])
        : new Set(['type', 'segmentId', 'messageId', 'text']);
      if (Object.keys(segment).some((key) => !allowedKeys.has(key))) return null;
      segmentIds.add(segment.segmentId);
      if (segment.type === 'assistant_text') {
        assistantText.push(segment.text);
        components.push({
          type: 'text',
          content: segment.text,
          timestamp,
          segmentId: segment.segmentId,
        });
        continue;
      }
      if (
        !MESSAGE_ID_RE.test(segment.messageId || '')
        || steeredMessageIds.has(segment.messageId)
      ) return null;
      steeredMessageIds.add(segment.messageId);
      components.push({
        type: 'steered_user_message',
        data: {
          content: segment.text,
          mode: 'steered',
          messageId: segment.messageId,
        },
        timestamp,
        segmentId: segment.segmentId,
      });
    }
    const derivedText = assistantText
      .filter((text) => text.length > 0)
      .join('\n\n');
    if (message.text !== derivedText) return null;
  }
  return {
    id: messageId,
    sender: message.sender,
    timestamp,
    app: message.app,
    text: message.text,
    content: message.text,
    components,
    rev: Number.isSafeInteger(message.revision) ? message.revision : undefined,
    sequence: Number.isSafeInteger(message.sequence) ? message.sequence : undefined,
    isStreaming: false,
    isError: message.isError === true,
    isCancelled: message.isCancelled === true,
    errorCode: typeof message.errorCode === 'string'
      ? message.errorCode
      : undefined,
  };
}

async function requestJson({
  chatId,
  authToken,
  method,
  body,
  baseUrl,
  fetchImpl = fetch,
  signal,
  requireScopedMode = true,
}) {
  const safeChatId = requireChatId(chatId);
  const safeAuthToken = requireAuthToken(authToken);
  const url = new URL(
    `${streamingBaseUrl(baseUrl)}/chats/${encodeURIComponent(safeChatId)}/transcript/messages`
  );

  const response = await fetchImpl(url.toString(), {
    method,
    headers: {
      Authorization: `Bearer ${safeAuthToken}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  const responseText = await response.text();
  if (utf8ByteLength(responseText) > MAX_SNAPSHOT_BYTES) {
    throw new ScopedTranscriptError(
      'transcript_snapshot_too_large',
      'The secure transcript snapshot exceeds the display limit.',
      response.status
    );
  }
  let data = {};
  try {
    data = responseText ? JSON.parse(responseText) : {};
  } catch {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript response is invalid.',
      response.status
    );
  }
  if (!response.ok) {
    throw new ScopedTranscriptError(
      data?.error || data?.code || 'secure_transcript_request_failed',
      data?.message || `Secure transcript request failed with status ${response.status}.`,
      response.status
    );
  }
  if (
    requireScopedMode
    && normalizeServerTranscriptMode(data?.mode) !== TRANSCRIPT_MODE_LOCKER_SCOPED
  ) {
    throw new ScopedTranscriptError(
      'transcript_scope_unverified',
      'The server did not verify the secure transcript scope.',
      response.status
    );
  }
  return data;
}

export async function fetchScopedTranscript({
  chatId,
  authToken,
  baseUrl,
  fetchImpl,
  signal,
}) {
  const data = await requestJson({
    chatId,
    authToken,
    method: 'GET',
    baseUrl,
    fetchImpl,
    signal,
  });
  if (!Array.isArray(data.messages)) {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript snapshot is invalid.'
    );
  }
  if (typeof data.truncated !== 'boolean') {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript snapshot is missing its truncation state.'
    );
  }
  if (
    data.accessFiltered !== undefined
    && typeof data.accessFiltered !== 'boolean'
  ) {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript snapshot has an invalid access state.'
    );
  }
  if (
    data.hiddenFromSequence !== undefined
    && data.hiddenFromSequence !== null
    && !Number.isSafeInteger(data.hiddenFromSequence)
  ) {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript snapshot has an invalid access boundary.'
    );
  }
  for (const message of data.messages) {
    if (utf8ByteLength(JSON.stringify(message)) > MAX_MESSAGE_BYTES) {
      throw new ScopedTranscriptError(
        'transcript_message_too_large',
        'A secure transcript message exceeds the display limit.'
      );
    }
  }
  const messages = data.messages.map(deserializeScopedTranscriptMessage);
  if (messages.some((message) => message === null)) {
    throw new ScopedTranscriptError(
      'malformed_transcript_snapshot',
      'The secure transcript snapshot contains an invalid message.'
    );
  }
  return {
    mode: TRANSCRIPT_MODE_LOCKER_SCOPED,
    messages,
    truncated: data.truncated,
    accessFiltered: data.accessFiltered === true,
    hiddenFromSequence: Number.isSafeInteger(data.hiddenFromSequence)
      ? data.hiddenFromSequence
      : null,
  };
}

export async function deleteScopedTranscript({
  chatId,
  authToken,
  baseUrl,
  fetchImpl,
  signal,
}) {
  const result = await requestJson({
    chatId,
    authToken,
    method: 'DELETE',
    baseUrl,
    fetchImpl,
    signal,
    requireScopedMode: false,
  });
  if (result?.deleted !== true) {
    throw new ScopedTranscriptError(
      'malformed_transcript_delete',
      'The secure transcript delete response is invalid.'
    );
  }
  return result;
}

export function subscribeScopedTranscript({
  chatId,
  getAuthToken,
  onMessages,
  onError,
  intervalMs = 1500,
  baseUrl,
  fetchImpl,
}) {
  if (typeof getAuthToken !== 'function' || typeof onMessages !== 'function') {
    throw new ScopedTranscriptError(
      'invalid_subscription',
      'A secure transcript subscription is invalid.'
    );
  }

  let cancelled = false;
  let timer = null;
  let controller = null;

  const poll = async () => {
    if (cancelled) return;
    controller = new AbortController();
    try {
      const authToken = await getAuthToken();
      const result = await fetchScopedTranscript({
        chatId,
        authToken,
        baseUrl,
        fetchImpl,
        signal: controller.signal,
      });
      if (cancelled) return;
      onMessages(result.messages, {
        truncated: result.truncated,
        accessFiltered: result.accessFiltered,
        hiddenFromSequence: result.hiddenFromSequence,
      });
    } catch (error) {
      if (!cancelled && error?.name !== 'AbortError') {
        onError?.(error);
        if (
          FATAL_SCOPE_STATUSES.has(error?.status)
          || FATAL_SCOPE_CODES.has(error?.code)
        ) {
          cancelled = true;
        }
      }
    } finally {
      controller = null;
      if (!cancelled) {
        timer = setTimeout(poll, intervalMs);
      }
    }
  };

  poll();
  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    controller?.abort();
  };
}
